import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";

import { Section, type SectionVariant } from "./Section";
import { config, mailtoHref, telHref, whatsappHref } from "../../config";

interface ContactSectionProps {
  variant?: SectionVariant;
}

interface ChannelRowProps {
  icon: ComponentType;
  label: string;
  value: string;
  href: string;
  ctaAriaLabel: string;
  external?: boolean;
}

function ChannelRow({ icon: Icon, label, value, href, ctaAriaLabel, external }: ChannelRowProps) {
  const externalProps = external
    ? { target: "_blank" as const, rel: "noreferrer noopener" }
    : {};

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2.5}
      component="a"
      href={href}
      {...externalProps}
      aria-label={ctaAriaLabel}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        textDecoration: "none",
        color: "inherit",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(253,247,238,0.98) 100%)",
        border: "1px solid rgba(201,163,90,0.35)",
        boxShadow: "0 8px 24px -18px rgba(62,44,20,0.35), inset 0 1px 0 rgba(255,255,255,0.7)",
        transition:
          "transform 220ms ease, border-color 220ms ease, background 220ms ease",
        ":hover": {
          transform: "translateY(-2px)",
          borderColor: "rgba(201,163,90,0.75)",
          background:
            "linear-gradient(135deg, rgba(248,236,214,0.98) 0%, rgba(255,255,255,0.98) 100%)",
        },
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          flexShrink: 0,
          background:
            "linear-gradient(135deg, rgba(232,199,137,0.28) 0%, rgba(232,199,137,0.14) 100%)",
          color: "#8F6D33",
          border: "1px solid rgba(201,163,90,0.5)",
        }}
      >
        <Icon />
      </Stack>
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 2,
            fontWeight: 600,
            color: "#8F6D33",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#2E2822",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      </Stack>
      <ArrowForwardRoundedIcon
        sx={{ color: "#A98544", opacity: 1, flexShrink: 0 }}
      />
    </Stack>
  );
}

export function ContactSection({ variant = "gold" }: ContactSectionProps = {}) {
  const { t } = useTranslation(["landing", "common"]);
  const emailSubject = `${config.brandName} — ${t("common:email.subjectQuote")}`;

  return (
    <Section
      id="contact"
      variant={variant}
      eyebrow={t("common:eyebrow.contact")}
      title={t("landing:contact.title")}
      subtitle={t("landing:contact.subtitle")}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(600px 320px at 85% 15%, rgba(232,199,137,0.28), transparent 60%), radial-gradient(480px 320px at 10% 90%, rgba(246,227,218,0.20), transparent 60%)",
        }}
      />

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 3, md: 6 },
          borderRadius: 5,
          color: "#2E2822",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,251,244,0.96) 50%, rgba(248,236,214,0.95) 100%)",
          border: "1px solid rgba(201,163,90,0.28)",
          backdropFilter: "blur(14px)",
          boxShadow:
            "0 30px 90px -45px rgba(62,44,20,0.35), inset 0 1px 0 rgba(255,255,255,0.7)",
        }}
      >
        <Typography
          aria-hidden
          sx={{
            position: "absolute",
            top: { xs: -36, md: -56 },
            right: { xs: -18, md: -24 },
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            fontSize: { xs: 180, md: 280 },
            lineHeight: 1,
            letterSpacing: -8,
            color: "rgba(201,163,90,0.14)",
            userSelect: "none",
          }}
        >
          B
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 4, md: 6 }}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          sx={{ position: "relative" }}
        >
          <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 500, lineHeight: 1.4, color: "#2E2822" }}
            >
              {t("landing:contact.pitch")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(46,40,34,0.72)",
                fontStyle: "italic",
                letterSpacing: 0.5,
              }}
            >
              {t("landing:contact.signature")}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ pt: 1, flexWrap: "wrap" }}>
              <Button
                size="large"
                variant="contained"
                startIcon={<EmailOutlinedIcon />}
                href={mailtoHref(emailSubject)}
                sx={{
                  px: 3,
                  backgroundColor: "#E8C789",
                  color: "#1A1613",
                  ":hover": { backgroundColor: "#D9B56E" },
                }}
              >
                {t("landing:contact.emailLabel")}
              </Button>
              <Button
                size="large"
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                href={whatsappHref()}
                target="_blank"
                rel="noreferrer noopener"
                sx={{
                  px: 3,
                  borderColor: "rgba(201,163,90,0.6)",
                  color: "#3A3027",
                  ":hover": {
                    borderColor: "#C9A35A",
                    backgroundColor: "rgba(201,163,90,0.12)",
                  },
                }}
              >
                {t("landing:contact.whatsappLabel")}
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: 3,
                fontWeight: 700,
                color: "#A98544",
                pl: 0.5,
              }}
            >
              {t("landing:contact.channelsTitle")}
            </Typography>
            <ChannelRow
              icon={EmailOutlinedIcon}
              label={t("landing:contact.emailLabel")}
              value={config.contactEmail}
              href={mailtoHref(emailSubject)}
              ctaAriaLabel={t("landing:contact.emailLabel")}
            />
            <ChannelRow
              icon={CallOutlinedIcon}
              label={t("landing:contact.phoneLabel")}
              value={config.contactPhone}
              href={telHref()}
              ctaAriaLabel={t("landing:contact.phoneLabel")}
            />
            <ChannelRow
              icon={WhatsAppIcon}
              label={t("landing:contact.whatsappLabel")}
              value={`+${config.whatsappNumber}`}
              href={whatsappHref()}
              ctaAriaLabel={t("landing:contact.whatsappLabel")}
              external
            />
          </Stack>
        </Stack>

        {(config.instagramUrl || config.facebookUrl) && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{
              position: "relative",
              mt: { xs: 4, md: 5 },
              pt: 3,
              borderTop: "1px solid rgba(201,163,90,0.28)",
            }}
          >
            <Typography variant="body2" sx={{ color: "rgba(46,40,34,0.75)" }}>
              {t("landing:contact.social")}
            </Typography>
            <Stack direction="row" spacing={1}>
              {config.instagramUrl && (
                <IconButton
                  component="a"
                  href={config.instagramUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={t("common:aria.instagram")}
                  sx={{
                    color: "#3A3027",
                    border: "1px solid rgba(201,163,90,0.45)",
                    ":hover": {
                      backgroundColor: "rgba(201,163,90,0.14)",
                      borderColor: "#C9A35A",
                    },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              )}
              {config.facebookUrl && (
                <IconButton
                  component="a"
                  href={config.facebookUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={t("common:aria.facebook")}
                  sx={{
                    color: "#3A3027",
                    border: "1px solid rgba(201,163,90,0.45)",
                    ":hover": {
                      backgroundColor: "rgba(201,163,90,0.14)",
                      borderColor: "#C9A35A",
                    },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>
        )}
      </Paper>
    </Section>
  );
}
